import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import isFunction from 'lodash/isFunction';
import { ButtonWithIcon } from '@els/els-react--button';
import RouteOverviewTreePopup from '@Pages/RetroSynthesis/RouteTree/components/RouteOverviewTreePopup';

import './MultiStepReactionDialog.scss';

function MultiStepReactionPreviewDialog({
  // from own props
  databaseId,
  rootReactionId,
  stepReactionIds,
  onClose,
  singleReaction,
  rtProjectId,
  rtRouteId,
  rtParentId,
  rtStepId,
  // from Redux
  isLoading,
  routeLayout,
  addProjectIsLoading,
  fetchMultiStepReactionPreview,
  submitMultiStepReactionProject,
  submitSingleStepReactionProject,
  enrollMultiStepScheme,
  multiStepReactionDialogClearState,
  // dialogs stuff
  selfClose,
  ...otherProps
}) {
  const history = useHistory();
  useEffect(() => {
    // Fetch data
    fetchMultiStepReactionPreview({ databaseId, rootReactionId, stepReactionIds });

    // Clean-up
    return () => {
      if (isFunction(onClose)) onClose();
      multiStepReactionDialogClearState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTransferHandler = async () => {
    if (addProjectIsLoading) {
      return;
    }
    if (rtStepId) {
      await enrollMultiStepScheme({
        rtProjectId,
        rtRouteId,
        rtParentId,
        rtStepId,
        routeLayout,
        history,
      });
    } else if (singleReaction) {
      await submitSingleStepReactionProject({ databaseId, reactionId: rootReactionId, history });
    } else {
      await submitMultiStepReactionProject({ databaseId, rootReactionId, stepReactionIds, history });
    }
    selfClose();
  };

  return (
    <RouteOverviewTreePopup
      {...otherProps}
      className="multi-step-reaction-dialog"
      routeName={`${singleReaction ? '' : 'Multi-step: '}Reaction ID ${rootReactionId}`}
      isLoading={isLoading}
      treeList={routeLayout}
      footerClassName="multi-step-reaction-dialog__footer"
      footer={
        <ButtonWithIcon
          data-e2e="transfer-to-synthesis"
          type="primary"
          sprite="ChevronRight"
          iconRight
          disabled={!routeLayout}
          className="e2e-transfer-to-synthesis"
          data-testid="transfer-btn"
          onClick={onTransferHandler}>
          {rtStepId ? 'Insert Multi-step scheme' : 'Create Retrosynthesis Plan'}
        </ButtonWithIcon>
      }
      selfClose={selfClose}
    />
  );
}

MultiStepReactionPreviewDialog.defaultProps = {
  onClose: () => {},
  fetchMultiStepReactionPreview: () => {},
  submitMultiStepReactionProject: () => {},
  submitSingleStepReactionProject: () => {},
  multiStepReactionDialogClearState: () => {},
  enrollMultiStepScheme: () => {},
  singleReaction: false,
};

MultiStepReactionPreviewDialog.propTypes = {
  databaseId: PropTypes.string,
  rootReactionId: PropTypes.string,
  stepReactionIds: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func,
  isLoading: PropTypes.bool,
  singleReaction: PropTypes.bool,
  routeLayout: PropTypes.array,
  addProjectIsLoading: PropTypes.bool,
  fetchMultiStepReactionPreview: PropTypes.func,
  submitMultiStepReactionProject: PropTypes.func,
  submitSingleStepReactionProject: PropTypes.func,
  multiStepReactionDialogClearState: PropTypes.func,
  enrollMultiStepScheme: PropTypes.func,
  rtProjectId: PropTypes.string,
  rtRouteId: PropTypes.string,
  rtParentId: PropTypes.string,
  rtStepId: PropTypes.string,
};

export default MultiStepReactionPreviewDialog;
